import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import * as S from './PromptUserInfo.style';

import { RootStoreContext } from '../../../App';

import useUserMutation from '../../../hooks/queries/useUserMutation';
import useUserFaceQuery from '../../../hooks/queries/useUserFaceQuery';
import useUserFaceMutation from '../../../hooks/queries/useUserFaceMutation';
import useQuestionAnswersQuery from '../../../hooks/queries/useQuestionAnswersQuery';
import { addProfile } from '../../../services/apis/AuthApi';
import { updateQuestionAnswers } from '../../../services/apis/QuestionAnswerApi';
import Image from '../../../components/image/Image';
import { css } from 'styled-components';
import Spinner from '../../../components/spinner/Spinner';
import regions from '../../../data/regions';

const FACE_IMAGE_ACCEPT = '.jpg,.jpeg,.png';
const FACE_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const FACE_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

const isValidFaceImageFile = (file) => {
	if (!file) return false;

	const fileExtension = file.name?.split('.').pop()?.toLowerCase();
	const hasValidExtension = FACE_IMAGE_EXTENSIONS.includes(fileExtension);
	const hasValidType = !file.type || FACE_IMAGE_TYPES.includes(file.type);

	return hasValidExtension && hasValidType;
};

const PromptUserInfo = observer(({ user, type, setUser, refetch }) => {
	const { authStore } = React.useContext(RootStoreContext);

	const id = user?.id;
	const [name, setName] = useState(user?.name ?? '');
	const [birthYear, setBirthYear] = useState(
		user?.birth?.toString().slice(0, 4) || '',
	);
	const [birthMonth, setBirthMonth] = useState(
		user?.birth?.toString().slice(4, 6) || '',
	);
	const [birthDay, setBirthDay] = useState(
		user?.birth?.toString().slice(6, 8) || '',
	);
	const [gender, setGender] = useState(user?.gender ?? 'MALE');
	const [profile, setProfile] = useState(user?.profile?.name ?? '');
	const [description, setDescription] = useState(user?.description ?? '');
	const [nickname, setNickname] = useState(
		user?.nickname ?? user?.nickName ?? user?.nick_name ?? '',
	);
	const [kidId, setKidId] = useState(user?.kidId ?? '');
	const [livingPlace, setLivingPlace] = useState(user?.livingPlace ?? '');
	const [livingPlaceParent, setLivingPlaceParent] = useState('');
	const [livingPlaceChild, setLivingPlaceChild] = useState('');
	const [questionAnswerValues, setQuestionAnswerValues] = useState({});
	const [loadingIndex, setLoadingIndex] = useState(0);

	const [checkList, setCheckList] = useState([]);

	const { data } = useUserFaceQuery(id, authStore.auth.collectionName);
	const { mutateAsync: faceMutateAsync, isLoading: isUserFaceLoading } =
		useUserFaceMutation();
	const userMutation = useUserMutation();
	const questionCategory = authStore.isKid
		? 'kid'
		: authStore.isSenior
		? 'senior'
		: undefined;
	const {
		data: questionAnswers,
		isLoading: isQuestionAnswersLoading,
		isError: isQuestionAnswersError,
	} = useQuestionAnswersQuery(id, questionCategory);

	const buildLivingPlace = (parent, child) =>
		[parent?.trim(), child?.trim()].filter(Boolean).join(' ');

	const parseLivingPlace = (value) => {
		if (!value) return { parent: '', child: '' };
		for (const region of regions) {
			if (value === region.name) return { parent: region.name, child: '' };
			for (const child of region.children) {
				if (
					value === `${region.name} ${child}` ||
					value === `${region.name}/${child}`
				) {
					return { parent: region.name, child };
				}
			}
		}
		const [parent, ...rest] = value.split(' ');
		return { parent: parent || '', child: rest.join(' ') || '' };
	};

	useEffect(() => {
		if (!Array.isArray(questionAnswers)) return;

		setQuestionAnswerValues(
			questionAnswers.reduce((answers, item) => {
				answers[item.code] = item.answer ?? '';
				return answers;
			}, {}),
		);
	}, [questionAnswers]);

	useEffect(() => {
		if (type === 'add') reset();
		else {
			setName(user?.name ?? '');
			setBirthYear(user?.birth?.toString().slice(0, 4) ?? '');
			setBirthMonth(user?.birth?.toString().slice(4, 6) ?? '');
			setBirthDay(user?.birth?.toString().slice(6, 8) ?? '');
			setGender(user?.gender ?? 'MALE');
			setProfile(user?.profile?.name ?? '');
			setDescription(user?.description ?? '');
			setNickname(user?.nickname ?? user?.nickName ?? user?.nick_name ?? '');
			setKidId(user?.kidId ?? '');
			const parsedPlace = parseLivingPlace(user?.livingPlace ?? '');
			setLivingPlace(user?.livingPlace ?? '');
			setLivingPlaceParent(parsedPlace.parent);
			setLivingPlaceChild(parsedPlace.child);
		}
	}, [type, user]);

	const reset = () => {
		setName('');
		setBirthYear('');
		setBirthMonth('');
		setBirthDay('');
		setGender('MALE');
		setProfile('');
		setDescription('');
		setNickname('');
		setKidId('');
		setLivingPlace('');
		setLivingPlaceParent('');
		setLivingPlaceChild('');
		setQuestionAnswerValues({});
	};

	useEffect(() => {
		const mergedPlace = buildLivingPlace(livingPlaceParent, livingPlaceChild);
		setLivingPlace(mergedPlace);
	}, [livingPlaceParent, livingPlaceChild]);

	useEffect(() => {
		const selectedRegion = regions.find(
			(region) => region.name === livingPlaceParent,
		);
		if (!selectedRegion) {
			if (livingPlaceChild) setLivingPlaceChild('');
			return;
		}
		if (
			livingPlaceChild &&
			!selectedRegion.children.includes(livingPlaceChild)
		) {
			setLivingPlaceChild('');
		}
	}, [livingPlaceParent, livingPlaceChild]);

	const check = () => {
		const checks = [];
		console.log('profile', profile);
		console.log('!profile', !profile);

		if (!name) checks.push('name');
		// if (!kidId) checks.push('kidId');
		if (!gender) checks.push('gender');
		if (birthYear.length < 4 || parseInt(birthYear) > new Date().getFullYear())
			checks.push('birthYear');
		if (
			birthMonth.length < 2 ||
			!(parseInt(birthMonth) > 0) ||
			!(parseInt(birthMonth) < 13)
		)
			checks.push('birthMonth');

		const dayRangeDate = new Date(birthYear, birthMonth, 0);
		const dayRange = dayRangeDate.getDate();
		if (
			birthDay.length < 2 ||
			!(parseInt(birthDay) > 0) ||
			!(parseInt(birthDay) < dayRange + 1)
		) {
			checks.push('birthDay');
		}
		console.log('aaaa', checks);
		setCheckList(checks);
		return checks;
	};

	const save = async () => {
		if (questionCategory && isQuestionAnswersLoading) {
			alert('질문을 불러온 후 다시 저장해 주세요.');
			return;
		}
		if (questionCategory && isQuestionAnswersError) {
			alert('질문을 불러오지 못했습니다. 다시 시도해 주세요.');
			return;
		}

		const checks = await check();
		if (checks.length === 0) {
			const mergedLivingPlace = buildLivingPlace(
				livingPlaceParent,
				livingPlaceChild,
			);
			let findProfile = authStore.auth.profile;
			console.log('authStore.auth.profiles', authStore.auth.profiles);
			console.log('authStore.auth.profiles11', profile);
			if (profile?.trim()) {
				findProfile = authStore.auth.profiles?.find((p) => p.name === profile);
			}
			if (!findProfile && profile?.trim()) {
				try {
					findProfile = await addProfile({ name: profile });
					authStore.updateProfiles(findProfile);
				} catch (e) {
					alert('새로운 반을 생성하는 데 실패했습니다.');
					return null;
				}
			}
			console.log('findProfile', findProfile);

			const data = {
				name,
				nickname,
				kidId,
				description,
				gender,
				birth: birthYear + birthMonth + birthDay,
				livingPlace: mergedLivingPlace,
				profileId:
					findProfile?.id?.toString() || authStore.auth.profile.id.toString(),
			};
			if (id) data.id = id;
			if (type === 'add') data.collectionName = authStore.auth.collectionName;
			console.log('save data', JSON.stringify(data));

			const result = await userMutation.mutateAsync({ type, data });
			if (result) {
				const savedFaceUserId =
					id ??
					result?.id ??
					result?.face_user_id ??
					result?.faceUserId ??
					(typeof result === 'number' || typeof result === 'string'
						? result
						: undefined);
				const answers = questionAnswers?.map((item) => ({
					question_id: item.question_id,
					answer: questionAnswerValues[item.code] ?? '',
				}));

				if (answers?.length) {
					if (!savedFaceUserId) {
						alert(
							'친구 정보는 저장되었지만 질문 답변 저장에 필요한 사용자 ID를 확인할 수 없습니다.',
						);
						setUser(null);
						refetch();
						return;
					}

					try {
						await updateQuestionAnswers({
							faceUserId: savedFaceUserId,
							answers,
						});
					} catch (error) {
						console.error('Failed to save question answers:', error);
						alert('친구 정보는 저장되었지만 질문 답변 저장에 실패했습니다.');
						setUser(null);
						refetch();
						return;
					}
				}

				console.log('tempUser', result, data);
				setUser(null);
				refetch();
			} else {
				alert('사용자 등록에 실패했습니다.');
			}
			// window.location.reload();
		}
	};

	const deleteUser = async () => {
		for (const item of data) {
			await deleteFaceFile(item.id);
		}
		const userData = {
			id,
			name,
			kidId,
			description,
			gender,
			birth: birthYear + birthMonth + birthDay,
		};

		await userMutation.mutateAsync({ type: 'delete', data: userData });
		setUser(null);
		refetch();
	};

	const handleFileChange = async (event) => {
		const file = event.target.files[0];
		if (!file) return false;

		if (!isValidFaceImageFile(file)) {
			alert('사진은 JPG, PNG 파일만 등록할 수 있습니다.');
			event.target.value = '';
			return false;
		}

		console.log('file', file);
		const result = await faceMutateAsync({
			data: {
				faceUserId: id,
				collectionName: authStore.auth.collectionName,
			},
			file,
		});

		console.log('result', result);
		event.target.value = '';
		return true;
	};

	const deleteFaceFile = async (id) => {
		console.log('id', id);
		await faceMutateAsync({
			data: { id },
			type: 'delete',
		});
	};

	const faces = data && (
		<>
			{data?.map((item, index) => (
				<S.FaceBox key={`${item.id}_${index}`}>
					{isUserFaceLoading && loadingIndex === index ? (
						<S.SpinnerWrap>
							<Spinner />
						</S.SpinnerWrap>
					) : (
						<Image
							className='face'
							srcArray={[item.path]}
							custom={css`
								object-fit: cover !important;
								width: 25% !important;
							`}
						/>
					)}

					<button
						onClick={() => {
							if (window.confirm('사진을 삭제하시겠습니까?')) {
								setLoadingIndex(index);
								deleteFaceFile(item.id);
							}
						}}>
						<Image srcArray={[S.remove]} />
					</button>
				</S.FaceBox>
			))}
			{Array.from({ length: 5 - data?.length }, (_, index) => (
				<S.FaceBox key={index}>
					{isUserFaceLoading && loadingIndex === data.length + index ? (
						<S.SpinnerWrap>
							<Spinner />
						</S.SpinnerWrap>
					) : (
						<Image
							srcArray={[S.folder]}
							custom={css`
								object-fit: cover;
								padding: 0.7rem 0 0.7rem 0.5rem;
								${({ theme }) => theme.common.wh('20%', '100%')};
							`}
						/>
					)}

					<label>
						<Image srcArray={[S.upload]} />
						<input
							type='file'
							accept={FACE_IMAGE_ACCEPT}
							onChange={async (e) => {
								if (isValidFaceImageFile(e.target.files[0])) {
									setLoadingIndex(data.length + index);
								}
								await handleFileChange(e);
							}}
						/>
					</label>
				</S.FaceBox>
			))}
		</>
	);

	console.log('result user', user);

	return (
		<S.Container>
			<S.Top>
				<h1>{type === 'add' ? '친구 정보 등록' : '친구 관리'}</h1>
			</S.Top>
			<S.TableGrid>
				<S.Table className='detailTable'>
					<thead></thead>
					<tbody>
						<tr>
							<th>
								{authStore.isSenior ? '성함' : '이름'}
								<sapn className='required'>*</sapn>
							</th>
							<td>
								<div>
									<input
										className={checkList.includes('name') ? 'error' : undefined}
										type='text'
										value={name}
										onChange={(e) => setName(e.target.value)}
									/>
								</div>
							</td>
						</tr>
						{authStore.isSenior && (
							<tr>
								<th>호칭</th>
								<td>
									<div className='stack'>
										<input
											id='seniorNickname'
											type='text'
											value={nickname}
											onChange={(e) => setNickname(e.target.value)}
										/>
										<label className='honorificHelp' htmlFor='seniorNickname'>
											리쿠가 성함과 함께 불러드릴 호칭을 입력해 주세요.
											<br />
											입력하지 않으면 성함만 불러드립니다.
											<br />
											ex. 어르신 / 할머니 / 할아버지 / 선생님 등
										</label>
									</div>
								</td>
							</tr>
						)}
						<tr>
							<th>
								성별<sapn className='required'>*</sapn>
							</th>
							<td>
								<div>
									<>
										남
										<input
											type='radio'
											name='gander'
											value='MALE'
											checked={gender === 'MALE'}
											onChange={(e) => setGender(e.target.value)}
										/>
									</>
									<>
										여
										<input
											type='radio'
											name='gander'
											value='FEMALE'
											checked={gender === 'FEMALE'}
											onChange={(e) => setGender(e.target.value)}
										/>
									</>
								</div>
							</td>
						</tr>
						<tr>
							<th>
								생년월일<sapn className='required'>*</sapn>
							</th>
							<td>
								<div className='column'>
									<div className='column'>
										<div>
											<input
												className={
													checkList.includes('birthYear') ? 'error' : undefined
												}
												type='number'
												value={birthYear}
												onChange={(e) => setBirthYear(e.target.value)}
											/>
											<span>년</span>
										</div>
										<div>
											<input
												className={
													checkList.includes('birthMonth') ? 'error' : undefined
												}
												type='number'
												value={birthMonth}
												onChange={(e) => setBirthMonth(e.target.value)}
											/>
											<span>월</span>
										</div>
										<div>
											<input
												className={
													checkList.includes('birthDay') ? 'error' : undefined
												}
												type='number'
												value={birthDay}
												onChange={(e) => setBirthDay(e.target.value)}
											/>
											<span>일</span>
										</div>
									</div>
									{checkList.some((str) => str.includes('birth')) && (
										<span className='errorInfo'>예시) 20xx년 xx월 xx일</span>
									)}
								</div>
							</td>
						</tr>
						<tr>
							<th>소속{authStore.isSenior ? '(기관)' : '(반)'}</th>
							<td>
								<div>
									<input
										className={
											checkList.includes('profile') ? 'error' : undefined
										}
										type='text'
										value={profile}
										onChange={(e) => setProfile(e.target.value)}
										list='profileList'
									/>
									<datalist id='profileList'>
										{authStore.auth.profiles?.map((profile) => {
											console.log(profile);
											return <option key={profile.id} value={profile.name} />;
										})}
									</datalist>
								</div>
							</td>
						</tr>
						<tr>
							<th>
								사는곳<sapn className='required'>*</sapn>
							</th>
							<td>
								<div className='livingPlaceSelects'>
									<select
										value={livingPlaceParent}
										onChange={(e) => setLivingPlaceParent(e.target.value)}>
										<option value=''>선택</option>
										{regions.map((region) => (
											<option key={region.name} value={region.name}>
												{region.name}
											</option>
										))}
									</select>
									<select
										value={livingPlaceChild}
										onChange={(e) => setLivingPlaceChild(e.target.value)}
										disabled={!livingPlaceParent}>
										<option value=''>선택</option>
										{(
											regions.find(
												(region) => region.name === livingPlaceParent,
											)?.children || []
										).map((child) => (
											<option key={child} value={child}>
												{child}
											</option>
										))}
									</select>
								</div>
							</td>
						</tr>
						<tr>
							<th>
								{authStore.isSenior ? '어르신 사진' : '친구 사진'}
								<sapn className='required'>*</sapn>
								<br />
								<span>
									{authStore.isSenior ? '로봇이 어르신을 ' : '리쿠가 친구를 '}
									인식할 때 사용됩니다.
								</span>
							</th>
							<td>
								{type === 'add' ? (
									<span>
										친구 등록 후, <br />
										사용하실 수 있습니다. <br />
										{/* eslint-disable-next-line react/no-unescaped-entities */}
										'저장' 버튼을 눌러주세요.
									</span>
								) : (
									faces
								)}
							</td>
						</tr>
					</tbody>
				</S.Table>
				<S.Table>
					<thead></thead>
					<tbody>
						{isQuestionAnswersLoading && (
							<tr>
								<td colSpan='2'>질문을 불러오는 중입니다.</td>
							</tr>
						)}
						{isQuestionAnswersError && (
							<tr>
								<td colSpan='2'>질문을 불러오지 못했습니다.</td>
							</tr>
						)}
						{questionAnswers?.map((item) => (
							<tr key={item.question_id}>
								<th className='narrow'>{item.title}</th>
								<td>
									<div className='stack'>
										<div>Q. {item.question}</div>
										<div>
											<input
												type='text'
												value={questionAnswerValues[item.code] ?? ''}
												placeholder={item.hint_text ?? ''}
												onChange={(e) =>
													setQuestionAnswerValues((values) => ({
														...values,
														[item.code]: e.target.value,
													}))
												}
											/>
										</div>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</S.Table>
			</S.TableGrid>
			<S.ButtonWrap>
				<button
					className={`${type === 'add' && 'hidden'}`}
					onClick={() => deleteUser()}>
					{authStore.isSenior ? '어르신 정보 삭제 ' : '친구 정보 삭제'}
				</button>
				<button onClick={() => reset()}>정보 초기화</button>
				<button className='close' onClick={() => setUser()}>
					취소
				</button>
				<button onClick={() => save()}>저장</button>
			</S.ButtonWrap>
		</S.Container>
	);
});

export default PromptUserInfo;
